import { defineStore } from "pinia";
import { UserService } from "../services/user.service";
import { useLookupStore } from "./LookupStore";

export const useUserStore = defineStore('user', {
  state: () => {
    return {
      user: undefined,
      companies: undefined,
      selectedCompany: undefined
    }
  },

  getters: {
    isAuthenticated(state) {
      return state.user && !!state.user.email;
    },

    isAuthenticatedJournalist(state) {
      return this.isAuthenticated && state.user.vendor === 0;
    },

    isAuthenticatedVendor(state) {
      return this.isAuthenticated && state.user.vendor === 1;
    },

    isAuthenticatedAdmin(state) {
      return this.isAuthenticated && state.user.role === 'admin';
    },

    hasManyCompanies(state) {
      return !!state.companies && state.companies.length > 1;
    },

    hasSelectedCompany(state) {
      return !!state.selectedCompany;
    },

    hasSelectedCompanyManyCategories(state) {
      return state.selectedCompany?.main_categories?.length > 1
    },

    selectedCompanyMainCategories(state) {
      const lookupStore = useLookupStore();
      const companyMainCategoryIds = state.selectedCompany?.main_categories || [];
      return lookupStore.matchingMainCategories(companyMainCategoryIds)
    },

    selectedCompanyName(state) {
      if (!state.isAuthenticated) {
        return '';
      }
      
      if (state.hasSelectedCompany) {
        return state.selectedCompany.name ? ' - ' + state.selectedCompany.name : '';
      }
      return state.user.company_name ? ' - ' + state.user.company_name : '';
    },

    getUserCountriesAsIdValues(state) {
      return (state.user?.countries || []).map(country => country.id);
    },

    hasMatchingCountry(state) {
      return (countryIds = []) => {
        // intersect user countries with image company countries
        const intersection = state.getUserCountriesAsIdValues.filter(value => countryIds.includes(value));
        return intersection.length > 0;
      }
    }
  },

  actions: {
    async verifyIfSignedIn() {
      if (this.isAuthenticated) {
        return;
      }

      this.user = await (new UserService()).verifyLogin();
      this.handleSignedInUserCompanies();
    },

    signOut() {
      return Promise.resolve().then(() => {
        return (new UserService).logout()        
      }) 
      .then(() => {
        this.$patch({
          user: undefined,
          companies: undefined,
          selectedCompany: undefined
        });
      })
    },

    async signIn(data) {
      this.user = await (new UserService()).login(data);
      this.handleSignedInUserCompanies();
    },

    handleSignedInUserCompanies() {
      if (!this.user) {
        return;
      }

      this.companies = this.user.companies
      
      if (this.user.vendor !== 1) {
        return;
      } 
      
      if (this.companies.length === 1) {
        this.setSelectedCompany(this.companies[0]);
        return;
      }

      const persistedSelectedCompanyId = this.getPersistedSelectedCompany();
      if (persistedSelectedCompanyId) {
        // försök hitta tidigare valt företag och använd det som selected
        const company = this.companies.find(company => company.id === persistedSelectedCompanyId)
        this.setSelectedCompany(company);
      }
    },

    setSelectedCompany(company) {
      this.selectedCompany = company;
      if (!!company) {
        sessionStorage.setItem('pr_selectedcompany', company.id);
        return;
      }
      sessionStorage.removeItem('pr_selectedcompany');
    },

    getPersistedSelectedCompany() {
      const persisted = sessionStorage.getItem('pr_selectedcompany') || undefined
      return persisted ? +persisted : undefined;
    }
  }
})